import { useState, useMemo } from "react";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";
import ScrollReveal from "@/components/base/ScrollReveal";

function getNext14Days() {
  const days: { date: Date; label: string; weekday: string }[] = [];
  const weekdays = ["日", "一", "二", "三", "四", "五", "六"];
  const today = new Date();
  for (let i = 1; i <= 14; i++) {
    const d = new Date(today);
    d.setDate(today.getDate() + i);
    const month = d.getMonth() + 1;
    const day = d.getDate();
    days.push({
      date: d,
      label: `${month}/${day}`,
      weekday: weekdays[d.getDay()],
    });
  }
  return days;
}

const timeSlots = [
  "09:00", "10:00", "11:00",
  "13:00", "14:00", "15:00", "16:00", "17:00",
];

export default function Contact() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [selectedDate, setSelectedDate] = useState<string>("");
  const [selectedTime, setSelectedTime] = useState<string>("");
  const next14Days = useMemo(() => getNext14Days(), []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");

    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const res = await fetch("https://readdy.ai/api/form/d7svn3qj1g8i0g4g5p1g", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: data.toString(),
      });

      if (res.ok) {
        setFormStatus("success");
        form.reset();
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-brand-black">
      <SEOHead
        title="聯絡我們 | 預約諮詢 | 保險門神"
        description="聯絡保險門神，加入 LINE 快速諮詢或填寫預約表單。提供台中總公司與北屯區總部據點資訊，一對一專業財務顧問服務。"
        keywords="聯絡保險門神,保險諮詢預約,財務顧問聯絡,LINE諮詢,保險規劃諮詢"
        canonicalPath="/contact"
        ogType="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "name": "聯絡保險門神 Insurance Guardian",
          "description": "預約諮詢，加入 LINE 快速諮詢或填寫預約表單。提供台中總公司與北屯區總部據點資訊。",
          "url": "__SITE_URL__/contact",
          "isPartOf": {
            "@type": "WebSite",
            "name": "保險門神 Insurance Guardian",
            "url": "__SITE_URL__/"
          }
        }}
      />
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-24 md:pt-32 pb-12 md:pb-16">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20dark%20luxury%20background%20with%20golden%20connection%20lines%20and%20communication%20symbols%20on%20deep%20black%20canvas%2C%20subtle%20warm%20golden%20glow%20radiating%20outward%20representing%20human%20connection%20and%20trust%2C%20premium%20brand%20aesthetic%20with%20elegant%20gold%20particles%20and%20light%20trails%2C%20sophisticated%20minimalist%20composition%2C%20cinematic%20lighting%2C%204K%20quality&width=1920&height=600&seq=206&orientation=landscape"
              alt="聯絡我們背景"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
          </div>
          <div className="container-main relative z-10 text-center">
            <div className="inline-block border border-brand-border rounded-full px-5 py-2 mb-6">
              <span className="text-brand-gold text-[1rem] font-medium tracking-wide">
                聯絡我們
              </span>
            </div>
            <h1 className="text-white font-display font-bold text-[2rem] md:text-[2.75rem] leading-[1.3] md:leading-[1.25] mb-4">
              聯絡我們
            </h1>
            <p className="text-brand-text-secondary text-[1.1875rem] leading-[1.75] max-w-[680px] mx-auto">
              無論你想整理保單、規劃資產、準備退休與傳承，或檢視企業風險，都可以先預約一次初談。
            </p>
            <div className="w-[60px] h-[2px] bg-brand-gold mx-auto mt-4" />
          </div>
        </section>

        {/* Content */}
        <section className="pb-20 md:pb-28">
          <div className="container-main">
            {/* Line Consultation Block */}
            <ScrollReveal className="bg-brand-card border border-brand-border rounded-2xl p-6 md:p-10 mb-10 md:mb-12 text-center">
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-xl bg-brand-gold/10 mx-auto mb-5">
                <span className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center text-brand-gold text-2xl md:text-3xl">
                  <i className="ri-line-fill" />
                </span>
              </div>
              <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4] mb-3">
                最快的諮詢方式
              </h2>
              <p className="text-brand-text-secondary text-[1.1875rem] leading-[1.75] mb-6 max-w-[520px] mx-auto">
                加入 LINE@，留下你的需求，我們將協助你安排初步健診。
              </p>
              <a
                href="https://lin.ee/o51JBHX"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-black px-10 py-4 rounded-full text-[1.125rem] font-semibold hover:bg-brand-gold-soft transition-colors whitespace-nowrap"
              >
                <i className="ri-line-fill" />
                加入 LINE@
              </a>
              <p className="text-brand-text-secondary/70 text-[1rem] mt-3">
                ID：@305fhcxf
              </p>
            </ScrollReveal>

            <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
              {/* Left: Contact Info + Social + Addresses */}
              <div className="lg:w-[40%] relative">
                <div
                  className="absolute top-0 left-0 w-[300px] h-[300px] rounded-full opacity-10"
                  style={{
                    background: "radial-gradient(circle, #D4AF37 0%, transparent 70%)",
                  }}
                />
                <div className="relative z-10 space-y-10">
                  {/* Brand */}
                  <ScrollReveal>
                    <div>
                      <h2 className="text-white font-display font-bold text-[1.5rem] md:text-[2.125rem] leading-[1.4] mb-4">
                        保險門神 Insurance Guardian
                      </h2>
                      <p className="text-brand-text-secondary text-[1.1875rem] leading-[1.75]">
                        先守護，再成長。從個人與家庭到企業與傳承，用專業把關每一步。
                      </p>
                    </div>
                  </ScrollReveal>

                  {/* Social Links */}
                  <ScrollReveal delay={100}>
                    <div>
                      <h3 className="text-white font-semibold text-[1.25rem] md:text-[1.625rem] mb-5">
                        社群連結
                      </h3>
                      <div className="flex flex-wrap gap-4">
                        <a
                          href="https://www.facebook.com/profile.php?id=61581854217759"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2.5 border border-brand-border text-white px-5 py-3 rounded-lg text-[1rem] font-medium hover:border-brand-gold hover:text-brand-gold transition-colors whitespace-nowrap"
                        >
                          <span className="w-5 h-5 flex items-center justify-center text-brand-gold">
                            <i className="ri-facebook-fill text-lg" />
                          </span>
                          Facebook
                        </a>
                        <a
                          href="https://www.instagram.com/nefertari_0122?igsh=c3o2MHVrd3owMTl5"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-2.5 border border-brand-border text-white px-5 py-3 rounded-lg text-[1rem] font-medium hover:border-brand-gold hover:text-brand-gold transition-colors whitespace-nowrap"
                        >
                          <span className="w-5 h-5 flex items-center justify-center text-brand-gold">
                            <i className="ri-instagram-fill text-lg" />
                          </span>
                          Instagram
                        </a>
                      </div>
                    </div>
                  </ScrollReveal>

                  {/* Addresses */}
                  <ScrollReveal delay={200}>
                    <div>
                      <h3 className="text-white font-semibold text-[1.25rem] md:text-[1.625rem] mb-5">
                        據點資訊
                      </h3>
                      <div className="space-y-5">
                        {/* Address 1 */}
                        <div className="bg-brand-black border border-brand-border rounded-xl p-5 md:p-6">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="w-5 h-5 flex items-center justify-center text-brand-gold">
                              <i className="ri-map-pin-line" />
                            </span>
                            <span className="text-brand-gold font-medium text-[1rem]">
                              台中總公司
                            </span>
                          </div>
                          <p className="text-white text-[1.0625rem] leading-[1.7] mb-3">
                            407695 台中市西屯區東大路一段398號
                          </p>
                          <a
                            href="https://www.google.com/maps/search/?api=1&query=407695+台中市西屯區東大路一段398號"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-brand-gold text-[0.9375rem] font-medium hover:underline"
                          >
                            <i className="ri-external-link-line" />
                            前往 Google Maps
                          </a>
                        </div>

                        {/* Address 2 */}
                        <div className="bg-brand-black border border-brand-border rounded-xl p-5 md:p-6">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="w-5 h-5 flex items-center justify-center text-brand-gold">
                              <i className="ri-map-pin-line" />
                            </span>
                            <span className="text-brand-gold font-medium text-[1rem]">
                              北屯區總部
                            </span>
                          </div>
                          <p className="text-white text-[1.0625rem] leading-[1.7] mb-3">
                            406 台中市北屯區文心路四段955號10F-2
                          </p>
                          <a
                            href="https://www.google.com/maps/search/?api=1&query=406+台中市北屯區文心路四段955號10F-2"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-brand-gold text-[0.9375rem] font-medium hover:underline"
                          >
                            <i className="ri-external-link-line" />
                            前往 Google Maps
                          </a>
                        </div>
                      </div>
                    </div>
                  </ScrollReveal>
                </div>
              </div>

              {/* Right: Form */}
              <ScrollReveal className="lg:w-[60%]" direction="right" delay={100}>
                <div className="bg-brand-card border border-brand-border rounded-2xl p-6 md:p-10">
                  <h2 className="text-white font-semibold text-[1.375rem] md:text-[1.625rem] mb-6">
                    諮詢表單
                  </h2>

                  <form
                    data-readdy-form
                    id="contact-consultation"
                    onSubmit={handleSubmit}
                    className="space-y-5"
                  >
                    <div>
                      <label htmlFor="name" className="block text-brand-text-secondary text-[1rem] mb-2">
                        姓名 <span className="text-brand-gold">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full bg-brand-black border border-brand-border rounded-lg px-4 py-3 text-white text-[1rem] focus:border-brand-gold focus:outline-none transition-colors"
                        placeholder="請輸入您的姓名"
                      />
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-brand-text-secondary text-[1rem] mb-2">
                        電話 <span className="text-brand-gold">*</span>
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        required
                        className="w-full bg-brand-black border border-brand-border rounded-lg px-4 py-3 text-white text-[1rem] focus:border-brand-gold focus:outline-none transition-colors"
                        placeholder="請輸入聯絡電話"
                      />
                    </div>

                    <div>
                      <label htmlFor="email" className="block text-brand-text-secondary text-[1rem] mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        className="w-full bg-brand-black border border-brand-border rounded-lg px-4 py-3 text-white text-[1rem] focus:border-brand-gold focus:outline-none transition-colors"
                        placeholder="請輸入電子郵件（選填）"
                      />
                    </div>

                    {/* Booking Slots */}
                    <div>
                      <label className="block text-brand-text-secondary text-[1rem] mb-3">
                        預約諮詢時段（選填）
                      </label>

                      {/* Date picker */}
                      <p className="text-brand-text-secondary/70 text-[0.9375rem] mb-2">
                        選擇日期
                      </p>
                      <div className="flex gap-2 overflow-x-auto pb-3 mb-4 scrollbar-thin">
                        {next14Days.map((d) => {
                          const dateStr = d.date.toISOString().split("T")[0];
                          const isSelected = selectedDate === dateStr;
                          return (
                            <button
                              key={dateStr}
                              type="button"
                              onClick={() => setSelectedDate(isSelected ? "" : dateStr)}
                              className={`flex flex-col items-center justify-center min-w-[64px] h-[72px] rounded-xl border transition-all duration-200 shrink-0 cursor-pointer ${
                                isSelected
                                  ? "border-brand-gold bg-brand-gold/15 text-brand-gold"
                                  : "border-brand-border bg-brand-black text-brand-text-secondary hover:border-brand-gold/40"
                              }`}
                            >
                              <span className="text-[0.75rem] opacity-80">{d.weekday}</span>
                              <span className="text-[1rem] font-semibold">{d.label}</span>
                            </button>
                          );
                        })}
                      </div>

                      {/* Time picker */}
                      <p className="text-brand-text-secondary/70 text-[0.9375rem] mb-2">
                        選擇時間
                      </p>
                      <div className="grid grid-cols-4 sm:grid-cols-8 gap-2">
                        {timeSlots.map((slot) => {
                          const isSelected = selectedTime === slot;
                          return (
                            <button
                              key={slot}
                              type="button"
                              onClick={() => setSelectedTime(isSelected ? "" : slot)}
                              className={`py-2.5 rounded-lg border text-[0.9375rem] font-medium transition-all duration-200 cursor-pointer ${
                                isSelected
                                  ? "border-brand-gold bg-brand-gold/15 text-brand-gold"
                                  : "border-brand-border bg-brand-black text-brand-text-secondary hover:border-brand-gold/40"
                              }`}
                            >
                              {slot}
                            </button>
                          );
                        })}
                      </div>

                      {/* Hidden inputs for form submission */}
                      <input type="hidden" name="preferred_date" value={selectedDate} />
                      <input type="hidden" name="preferred_time" value={selectedTime} />
                    </div>

                    <div>
                      <label htmlFor="topic" className="block text-brand-text-secondary text-[1rem] mb-2">
                        想諮詢的項目 <span className="text-brand-gold">*</span>
                      </label>
                      <select
                        id="topic"
                        name="topic"
                        required
                        className="w-full bg-brand-black border border-brand-border rounded-lg px-4 py-3 text-white text-[1rem] focus:border-brand-gold focus:outline-none transition-colors appearance-none"
                      >
                        <option value="">請選擇諮詢項目</option>
                        <option value="policy">保單門神</option>
                        <option value="asset">財產門神</option>
                        <option value="life">人生門神</option>
                        <option value="enterprise">企業門神</option>
                        <option value="other">其他</option>
                      </select>
                    </div>

                    <div>
                      <label htmlFor="message" className="block text-brand-text-secondary text-[1rem] mb-2">
                        備註 / 目前遇到的問題
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        maxLength={500}
                        className="w-full bg-brand-black border border-brand-border rounded-lg px-4 py-3 text-white text-[1rem] focus:border-brand-gold focus:outline-none transition-colors resize-none"
                        placeholder="請簡述您的需求或問題（限 500 字）"
                      />
                      <p className="text-brand-text-secondary/70 text-[0.9375rem] mt-1">
                        最多 500 字
                      </p>
                    </div>

                    <button
                      type="submit"
                      disabled={formStatus === "submitting"}
                      className="w-full bg-brand-gold text-brand-black py-4 rounded-full text-[1.125rem] font-semibold hover:bg-brand-gold-soft transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      {formStatus === "submitting" ? "送出中..." : "送出諮詢"}
                    </button>

                    {formStatus === "success" && (
                      <p className="text-brand-gold text-[1rem] text-center">
                        預約成功！我們會盡快與您聯絡。
                      </p>
                    )}
                    {formStatus === "error" && (
                      <p className="text-red-400 text-[1rem] text-center">
                        送出失敗，請稍後再試或直接加入 LINE 諮詢。
                      </p>
                    )}
                  </form>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}